/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `CommercialOffer` for the Caintra Connect frontend.
 * @module components/CompanyProfileSections/ThirdTab/CommercialOffer
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import "../index.css";
import { useTranslation } from "react-i18next";
import {
  getExportsOptions,
  getImportsOptions,
  getSellsToGovernmentOptions,
  getCountryExportingToOptions,
  getCountryImportingFromOptions,
} from "../../../data/selectOptions";
import { getSelectedOption, getCompanyFieldValue } from "../../../utils/common";

const CommercialOffer = ({ company, selectedProfile = 0 }) => {
  const { t } = useTranslation();

  const exports = getCompanyFieldValue(
    company,
    "exporta",
    selectedProfile,
    null,
  );
  const countryExportingTo = getCompanyFieldValue(
    company,
    "paisExportacion",
    selectedProfile,
    null,
  );
  const imports = getCompanyFieldValue(
    company,
    "importa",
    selectedProfile,
    null,
  );
  const countryImportingFrom = getCompanyFieldValue(
    company,
    "paisImportacion",
    selectedProfile,
    null,
  );
  const sellsToGovernment = getCompanyFieldValue(
    company,
    "vendeAGobierno",
    selectedProfile,
    null,
  );
  const rawMaterial = getCompanyFieldValue(
    company,
    "materiaPrima",
    selectedProfile,
    "",
  );

  const exportsOptions = getExportsOptions(t);
  const countryExportingToOptions = getCountryExportingToOptions(t);
  const importsOptions = getImportsOptions(t);
  const countryImportingFromOptions = getCountryImportingFromOptions(t);
  const sellsToGovernmentOptions = getSellsToGovernmentOptions(t);

  const getSelectedExportOption = getSelectedOption(exportsOptions, exports);
  const getSelectedCountryExportingToOption = getSelectedOption(
    countryExportingToOptions,
    countryExportingTo,
  );
  const getSelectedImportOption = getSelectedOption(importsOptions, imports);
  const getSelectedCountryImportingFromOption = getSelectedOption(
    countryImportingFromOptions,
    countryImportingFrom,
  );
  const getSelectedSellsToGovernmentOption = getSelectedOption(
    sellsToGovernmentOptions,
    sellsToGovernment,
  );

  return (
    <div className="profileSection">
      <h2 className="sectionTitle">
        {t("pages.companyProfile.commercialOffer")}
      </h2>

      <div className="gridContainer">
        <div className="gridRow">
          <div className="gridItem">
            <span className="gridLabel">
              {t("pages.companyProfile.exports")}
            </span>
            <span className="gridValue">
              {getSelectedExportOption?.label || ""}
            </span>
          </div>
          <div className="gridItem">
            <span className="gridLabel">
              {t("pages.companyProfile.countryExportingTo")}
            </span>
            <span className="gridValue">
              {getSelectedCountryExportingToOption?.label || ""}
            </span>
          </div>
          <div className="gridItem">
            <span className="gridLabel">
              {t("pages.companyProfile.imports")}
            </span>
            <span className="gridValue">
              {getSelectedImportOption?.label || ""}
            </span>
          </div>
          <div className="gridItem">
            <span className="gridLabel">
              {t("pages.companyProfile.countryImportingFrom")}
            </span>
            <span className="gridValue">
              {getSelectedCountryImportingFromOption?.label || ""}
            </span>
          </div>
        </div>

        <div className="gridRow">
          <div className="gridItem">
            <span className="gridLabel">
              {t("pages.companyProfile.sellsToGovernment")}
            </span>
            <span className="gridValue">
              {getSelectedSellsToGovernmentOption?.label || ""}
            </span>
          </div>
          <div className="gridItem">
            <span className="gridLabel">
              {t("pages.companyProfile.rawMaterial")}
            </span>
            <span className="gridValue">{rawMaterial || ""}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialOffer;
